import { excludeAbortError, isAbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { getNotificationSubscriptionId } from '@app/utils/getNotificationSubscriptionId'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { NotificationTopicGroup } from '@app/packages/internalApi/notificationTypes'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

/** ApiNotificationSubscription topic and channel joined with `:` */
type SubscriptionID = string

type ListState = {
  list: Record<SubscriptionID, boolean>
  meta: Record<SubscriptionID, { loading: boolean; error: Error | null } | undefined>
}

type SchemaState = {
  list: NotificationTopicGroup[]
  meta: { error: Error | null; loaded: boolean }
}

const notificationSubscriptionsListSlice = createReduxSlice<ListState>('notification_subscriptions_list')

const notificationSubscriptionsSchemaSlice = createReduxSlice<SchemaState>('notification_subscriptions_schema')

/** The list arriving is also what marks the screen loaded — the scheme itself comes from the internal api. */
export const fetchNotificationSubscriptions = () =>
  createThunk(async dispatch => {
    const [{ getNotificationSubscriptions }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/notification_subscriptions'),
      import('@app/store/actions/api'),
    ])
    const resp = await dispatch(callV2Api(getNotificationSubscriptions())).catch(catchFetchDataError(dispatch, 'Notification settings fetch failed'))
    dispatch(
      notificationSubscriptionsListSlice.update(state => ({
        meta: state?.meta ?? {},
        list: resp.data.reduce<Record<SubscriptionID, boolean>>((acc, n) => {
          acc[getNotificationSubscriptionId(n.attributes)] = true
          return acc
        }, {}),
      }))
    )
    dispatch(notificationSubscriptionsSchemaSlice.update(state => ({ list: state?.list ?? [], meta: { error: null, loaded: true } })))
  })

/** Shows the switch in its new position while the call is in flight. */
export const startNotificationSubscriptionToggleSlice = (id: SubscriptionID, subscribed: boolean) =>
  notificationSubscriptionsListSlice.update(state =>
    state ? { ...state, list: { ...state.list, [id]: subscribed }, meta: { ...state.meta, [id]: { loading: true, error: null } } } : state
  )

export const finishNotificationSubscriptionToggleSlice = (id: SubscriptionID) =>
  notificationSubscriptionsListSlice.update(state => (state ? { ...state, meta: { ...state.meta, [id]: { loading: false, error: null } } } : state))

/** An abort leaves the switch where it is: another toggle for the same topic is already on its way. */
export const failNotificationSubscriptionToggleSlice = (id: SubscriptionID, subscribed: boolean, error: Error) =>
  notificationSubscriptionsListSlice.update(state => {
    if (!state) return state
    const meta = { ...state.meta, [id]: { loading: false, error: excludeAbortError(error) } }
    if (isAbortError(error)) return { ...state, meta }
    return { ...state, list: { ...state.list, [id]: subscribed }, meta }
  })

export const setNotificationScheme = (list: NotificationTopicGroup[]) =>
  notificationSubscriptionsSchemaSlice.update(state => ({ meta: state?.meta ?? { error: null, loaded: false }, list }))

export const notificationSchemeSelector = createSelector([notificationSubscriptionsSchemaSlice.selector], schema => schema?.list ?? [])
export const notificationSubscriptionsMetaSelector = createSelector(
  [notificationSubscriptionsSchemaSlice.selector],
  schema => schema?.meta ?? { error: null, loaded: false }
)
export const notificationSubscriptionsListSelector = createSelector([notificationSubscriptionsListSlice.selector], state => state?.list ?? {})
export const notificationSubscriptionsListMetaSelector = createSelector([notificationSubscriptionsListSlice.selector], state => state?.meta ?? {})
